import React, { ReactElement, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { BillSummary } from '@enview/interface/types/bills/BillSummary';
import Plus from '../../../components/svg/Plus';
import ArrowDown from '../../../components/svg/ArrowDown';
import ConfirmActionPopover from '../Popovers/ConfirmActionPopover';
import TeamMemberSelectorContent from '../DropDowns/TeamMemberSelectorContent';
import WorkspacePicker from '../DropDowns/WorkspacePicker';
import { TeamAPI, BillTriageAPI } from '../../../api';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector, {
  getPrimaryWorkspaceId,
} from '../../../helpers/TeamHelper';
import {
  getAvatarColorClass,
  getDisplayName,
  getInitials,
} from '../../../helpers/AvatarHelper';
import './AssigneeColumnField.scss';

const MAX_VISIBLE_AVATARS = 3;

interface AssigneeColumnFieldProps {
  billId: string;
  billWorkspaces?: BillSummary['workspaces'];
  customFields?: BillSummary['customFields'];
}

const AssigneeColumnField = (props: AssigneeColumnFieldProps): ReactElement => {
  const { billId, billWorkspaces = [], customFields } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const currentTeamModeId = useSelector(getTeamMode);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const billWorkspaceIds = billWorkspaces
    .map((workspace) => workspace.id)
    .filter((id): id is number => id !== undefined);
  const teamId = getPrimaryWorkspaceId(
    userWorkspaces,
    billWorkspaceIds,
    currentTeamModeId,
  );

  const assigneeIds =
    (orgId && teamId
      ? (customFields?.[orgId]?.[teamId]?.Assignee?.fieldValue as number[] | undefined)
      : undefined) ?? [];

  const { data: teamMembers = [] } = TeamAPI.endpoints.getTeamMembers.useQuery(
    teamId ?? skipToken,
  );
  const assignedMembers = useMemo(
    () =>
      teamMembers.filter((member) => assigneeIds.includes(member.organizationUserId)),
    [teamMembers, assigneeIds],
  );

  const [assignBill, { isLoading: assigning }] =
    BillTriageAPI.endpoints.assignBill.useMutation();
  const [setWorkspaces, { isLoading: workspacesSaving }] =
    BillTriageAPI.endpoints.setBillWorkspaces.useMutation();
  const isMutating = assigning || workspacesSaving;

  // Assignee ids picked while the bill isn't in any of the user's workspaces yet are
  // held here and applied once the forced-open WorkspacePicker below resolves one.
  const [pendingAssigneeIds, setPendingAssigneeIds] = useState<number[] | null>(null);

  const [displayed, setDisplayed] = useState(false);

  const applyAssignees = async (ids: number[], targetTeamId: number): Promise<void> => {
    if (!orgId) return;
    await assignBill({
      orgId,
      billId,
      teamId: targetTeamId,
      assigneeUserIds: ids,
    }).unwrap();
  };

  const handleSave = (ids: number[]): void => {
    setDisplayed(false);
    if (!orgId || isMutating) return;
    if (!teamId) {
      setPendingAssigneeIds(ids);
      return;
    }
    void applyAssignees(ids, teamId);
  };

  const handleSaveWorkspaces = async (workspaceIds: number[]): Promise<void> => {
    if (!orgId) return;
    await setWorkspaces({ orgId, billId, teamIds: workspaceIds }).unwrap();

    if (pendingAssigneeIds) {
      const newTeamId = getPrimaryWorkspaceId(
        userWorkspaces,
        workspaceIds,
        currentTeamModeId,
      );
      if (newTeamId) await applyAssignees(pendingAssigneeIds, newTeamId);
      setPendingAssigneeIds(null);
    }
  };

  const visibleMembers = assignedMembers.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = assignedMembers.length - visibleMembers.length;

  const trigger = (
    <div className={`asf-trigger ${isMutating ? 'asf-trigger-disabled' : ''}`}>
      {assignedMembers.length === 0 ? (
        <span className="asf-assign-pill">
          <Plus /> Assign
        </span>
      ) : (
        <span className="asf-avatars">
          {visibleMembers.map((member) => (
            <span
              className={`asf-avatar ${getAvatarColorClass(member.organizationUserId)}`}
              key={member.organizationUserId}
              title={getDisplayName(member)}
            >
              {getInitials(member)}
            </span>
          ))}
          {overflowCount > 0 && (
            <span className="asf-avatar asf-avatar-overflow">+{overflowCount}</span>
          )}
        </span>
      )}
    </div>
  );

  return (
    <div className="assignee-field">
      <ConfirmActionPopover
        align="start"
        className="assignee-field-popover"
        closePopup={() => setDisplayed(false)}
        content={
          displayed ? (
            <TeamMemberSelectorContent
              assigneeIds={assigneeIds}
              members={teamMembers}
              onSave={handleSave}
              saving={isMutating}
            />
          ) : null
        }
        hideCloseIcon
        onClickOutside={() => setDisplayed(false)}
        positions={['bottom', 'top', 'right', 'left']}
        show={displayed}
        showArrowContainer={false}
        target={trigger}
        toggleCallback={() => !isMutating && setDisplayed((prev) => !prev)}
      />

      <WorkspacePicker
        billWorkspaceIds={billWorkspaceIds}
        forceOpen={!!pendingAssigneeIds}
        onForceClose={() => setPendingAssigneeIds(null)}
        onSave={handleSaveWorkspaces}
        sectionLabel="APPLY ASSIGNEE IN..."
        trigger={<ArrowDown className="asf-workspace-caret" />}
        workspaces={userWorkspaces}
      />
    </div>
  );
};

export default AssigneeColumnField;
